import { useState, useEffect, useCallback, useRef } from 'react';
import { getClients, getInvoice, createInvoice, updateInvoice, sendInvoice, createPayment, getBookingsLifecycle, createInvoiceFromBookings, transferBooking, getClientCredits, applyCredit, getInvoiceDetail, archivePaidInvoices, addBookingsToInvoice, getClientDraftInvoices, getInvoicePdfUrl } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Plus, Clock, CheckCircle2, FileText, Send, DollarSign, Edit, Percent, UserPlus, AlertTriangle, Trash2, CreditCard, ChevronDown, ChevronRight, Wallet, Archive, Eye, X, Download } from 'lucide-react';
import { format, parseISO, subWeeks, subMonths } from 'date-fns';

const COLUMNS = [
  { id: 'upcoming', title: 'Upcoming Sessions', color: 'bg-blue-500', description: 'Scheduled sessions' },
  { id: 'complete', title: 'Completed Sessions', color: 'bg-amber-500', description: 'Ready to invoice' },
  { id: 'invoiced', title: 'Draft Invoices', color: 'bg-purple-500', description: 'Not yet sent to client' },
  { id: 'outstanding', title: 'Outstanding Amount', color: 'bg-rose-500', description: 'Outstanding Client Balances' },
  { id: 'prepaid_credit', title: 'Pre Paid / Credit', color: 'bg-teal-500', description: 'Prepayments & credits' },
  { id: 'paid', title: 'Paid Invoices', color: 'bg-emerald-500', description: 'Recently paid' },
];

const PAYMENT_METHODS = [
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'cash', label: 'Cash' },
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'other', label: 'Other' },
];

const PAID_FILTERS = [
  { value: 'all', label: 'All time' },
  { value: '1w', label: 'Last 1 week' },
  { value: '2w', label: 'Last 2 weeks' },
  { value: '1m', label: 'Last 1 month' },
  { value: '3m', label: 'Last 3 months' },
];

// Persist expand state in localStorage
const EXPAND_KEY = 'bokr_kanban_expanded';
const HIDDEN_COLS_KEY = 'bokr_kanban_hidden_cols';
const getPersistedExpanded = () => {
  try { return JSON.parse(localStorage.getItem(EXPAND_KEY) || '{}'); } catch { return {}; }
};
const persistExpanded = (state) => {
  try { localStorage.setItem(EXPAND_KEY, JSON.stringify(state)); } catch {}
};
const getPersistedHiddenCols = () => {
  try { return JSON.parse(localStorage.getItem(HIDDEN_COLS_KEY) || '[]'); } catch { return []; }
};
const persistHiddenCols = (cols) => {
  try { localStorage.setItem(HIDDEN_COLS_KEY, JSON.stringify(cols)); } catch {}
};

export default function InvoicesPage() {
  const [lifecycleData, setLifecycleData] = useState({
    upcoming: [], complete: [], invoiced: [], outstanding: [], prepaid_credit: [], paid: []
  });
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBookings, setSelectedBookings] = useState({});
  const [paidFilter, setPaidFilter] = useState('1m');
  const [expandedCards, setExpandedCards] = useState(() => getPersistedExpanded());
  const [hiddenColumns, setHiddenColumns] = useState(() => getPersistedHiddenCols());

  // Toggle column visibility
  const toggleColumnVisibility = (colId) => {
    setHiddenColumns(prev => {
      const newHidden = prev.includes(colId) ? prev.filter(c => c !== colId) : [...prev, colId];
      persistHiddenCols(newHidden);
      return newHidden;
    });
  };

  // Create invoice
  const [createInvoiceOpen, setCreateInvoiceOpen] = useState(false);
  const [invoiceClientId, setInvoiceClientId] = useState('');
  const [invoiceBookingIds, setInvoiceBookingIds] = useState([]);

  // Edit invoice
  const [editInvoiceOpen, setEditInvoiceOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [invoiceDiscount, setInvoiceDiscount] = useState('');
  const [invoiceDiscountType, setInvoiceDiscountType] = useState('percent');
  const [invoiceDiscountDesc, setInvoiceDiscountDesc] = useState('');
  const [invoiceNotes, setInvoiceNotes] = useState('');

  // Payment
  const [paymentSheetOpen, setPaymentSheetOpen] = useState(false);
  const [payingInvoice, setPayingInvoice] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('bank_transfer');
  const [paymentMethodOther, setPaymentMethodOther] = useState('');
  const [paymentReference, setPaymentReference] = useState('');

  // Apply credit
  const [applyCreditOpen, setApplyCreditOpen] = useState(false);
  const [applyCreditInvoice, setApplyCreditInvoice] = useState(null);
  const [clientCredits, setClientCredits] = useState([]);
  const [selectedCreditId, setSelectedCreditId] = useState('');
  const [creditApplyAmount, setCreditApplyAmount] = useState('');

  // Transfer
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferringBooking, setTransferringBooking] = useState(null);
  const [transferTargetClient, setTransferTargetClient] = useState('');

  // Manual invoice
  const [manualInvoiceOpen, setManualInvoiceOpen] = useState(false);
  const [manualInvoiceClient, setManualInvoiceClient] = useState('');
  const [manualInvoiceItems, setManualInvoiceItems] = useState([{ description: '', quantity: 1, unit_price: '' }]);
  const [manualInvoiceNotes, setManualInvoiceNotes] = useState('');
  const [manualInvoiceIsPrepayment, setManualInvoiceIsPrepayment] = useState(false);

  // Invoice detail view
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailData, setDetailData] = useState(null);

  // Add to draft invoice
  const [addToDraftOpen, setAddToDraftOpen] = useState(false);
  const [addToDraftClientId, setAddToDraftClientId] = useState('');
  const [addToDraftBookingIds, setAddToDraftBookingIds] = useState([]);
  const [draftInvoices, setDraftInvoices] = useState([]);
  const [selectedDraftId, setSelectedDraftId] = useState('');

  // Drag
  const [draggedItem, setDraggedItem] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [lifecycle, clientsData] = await Promise.all([getBookingsLifecycle(), getClients()]);
      setLifecycleData(lifecycle);
      setClients(clientsData);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Persist expanded state
  useEffect(() => { persistExpanded(expandedCards); }, [expandedCards]);

  const toggleExpand = (id) => {
    setExpandedCards(prev => {
      const next = { ...prev, [id]: !prev[id] };
      return next;
    });
  };

  const isExpanded = (id) => !!expandedCards[id];

  const calculateDiscountCents = (subtotalCents) => {
    if (!invoiceDiscount || isNaN(parseFloat(invoiceDiscount))) return 0;
    const value = parseFloat(invoiceDiscount);
    return invoiceDiscountType === 'percent' ? Math.round((subtotalCents * value) / 100) : Math.round(value * 100);
  };

  const formatMoney = (cents) => `$${(Math.abs(cents || 0) / 100).toFixed(2)}`;

  const getDisplayName = (item) => {
    if (item.client_type === 'business' && item.client_business_name) return item.client_business_name;
    return item.client_name || 'Unknown';
  };
  const getSubtitle = (item) => {
    if (item.client_type === 'business' && item.client_business_name) return item.client_contact_person || item.client_name;
    return null;
  };

  // ---- Selection ----
  const toggleBookingSelection = (clientId, bookingId) => {
    setSelectedBookings(prev => {
      const current = prev[clientId] || [];
      return { ...prev, [clientId]: current.includes(bookingId) ? current.filter(id => id !== bookingId) : [...current, bookingId] };
    });
  };
  const selectAllForClient = (clientId, bookingIds) => {
    setSelectedBookings(prev => ({ ...prev, [clientId]: (prev[clientId] || []).length === bookingIds.length ? [] : [...bookingIds] }));
  };

  // ---- Actions ----
  const openCreateInvoice = (clientId, bookingIds) => { setInvoiceClientId(clientId); setInvoiceBookingIds(bookingIds); setCreateInvoiceOpen(true); };

  const handleCreateInvoice = async () => {
    if (!invoiceBookingIds.length) { toast.error('Select at least one booking'); return; }
    try {
      await createInvoiceFromBookings({ client_id: invoiceClientId, booking_ids: invoiceBookingIds });
      toast.success('Invoice created');
      setCreateInvoiceOpen(false);
      setSelectedBookings(prev => ({ ...prev, [invoiceClientId]: [] }));
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleEditInvoice = async (invoiceId) => {
    try {
      const inv = await getInvoice(invoiceId);
      setEditingInvoice(inv);
      setInvoiceDiscountType('fixed');
      setInvoiceDiscount(inv.discount_cents ? (inv.discount_cents / 100).toString() : '');
      setInvoiceDiscountDesc(inv.discount_description || '');
      setInvoiceNotes(inv.notes || '');
      setEditInvoiceOpen(true);
    } catch { toast.error('Failed to load invoice'); }
  };

  const handleSaveInvoice = async () => {
    if (!editingInvoice) return;
    try {
      await updateInvoice(editingInvoice.id, { discount_cents: calculateDiscountCents(editingInvoice.subtotal_cents), discount_description: invoiceDiscountDesc || null, notes: invoiceNotes || null });
      toast.success('Invoice updated');
      setEditInvoiceOpen(false);
      fetchData();
    } catch { toast.error('Failed to update invoice'); }
  };

  const handleSendInvoice = async (invoiceId) => {
    try { 
      const result = await sendInvoice(invoiceId); 
      if (result.email_sent) {
        toast.success(`Invoice sent to ${result.recipient_email}`);
      } else {
        toast.warning(result.message || 'Invoice marked as sent (no email delivered)');
      }
      fetchData(); 
    }
    catch { toast.error('Failed to send invoice'); }
  };

  const openPaymentSheet = async (invoiceId) => {
    try {
      const inv = await getInvoice(invoiceId);
      setPayingInvoice(inv);
      setPaymentAmount(((inv.total_cents - (inv.amount_paid_cents || 0) - (inv.credit_applied_cents || 0)) / 100).toFixed(2));
      setPaymentMethod('bank_transfer');
      setPaymentMethodOther('');
      setPaymentReference('');
      setPaymentSheetOpen(true);
    } catch { toast.error('Failed to load invoice'); }
  };

  const handleRecordPayment = async () => {
    if (!payingInvoice || !paymentAmount) return;
    try {
      await createPayment({
        client_id: payingInvoice.client_id, invoice_id: payingInvoice.id,
        amount_cents: Math.round(parseFloat(paymentAmount) * 100),
        method: paymentMethod, method_other: paymentMethod === 'other' ? paymentMethodOther : null,
        reference: paymentReference || null,
      });
      const paid = Math.round(parseFloat(paymentAmount) * 100);
      const remaining = payingInvoice.total_cents - (payingInvoice.amount_paid_cents || 0) - (payingInvoice.credit_applied_cents || 0);
      toast.success(paid < remaining ? 'Part payment recorded' : paid > remaining ? 'Payment recorded. Overpayment credited.' : 'Payment recorded');
      setPaymentSheetOpen(false);
      fetchData();
    } catch { toast.error('Failed to record payment'); }
  };

  const openApplyCredit = async (invoiceGroup) => {
    try {
      const credits = await getClientCredits(invoiceGroup.client_id);
      if (!credits.length) { toast.error('No credits available'); return; }
      const inv = await getInvoice(invoiceGroup.invoice_id);
      setApplyCreditInvoice(inv);
      setClientCredits(credits);
      setSelectedCreditId(credits[0]?.id || '');
      const remaining = inv.total_cents - (inv.amount_paid_cents || 0) - (inv.credit_applied_cents || 0);
      setCreditApplyAmount((Math.min(credits[0]?.amount_cents || 0, remaining) / 100).toFixed(2));
      setApplyCreditOpen(true);
    } catch { toast.error('Failed to load credits'); }
  };

  const handleApplyCredit = async () => {
    if (!selectedCreditId || !applyCreditInvoice) return;
    try {
      const r = await applyCredit({ credit_id: selectedCreditId, invoice_id: applyCreditInvoice.id, amount_cents: Math.round(parseFloat(creditApplyAmount) * 100) });
      toast.success(r.invoice_fully_paid ? 'Credit applied - invoice paid' : 'Credit applied');
      setApplyCreditOpen(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleTransfer = async () => {
    if (!transferringBooking || !transferTargetClient) return;
    try {
      await transferBooking(transferringBooking.id, transferTargetClient);
      toast.success('Booking transferred');
      setTransferOpen(false);
      fetchData();
    } catch { toast.error('Failed to transfer'); }
  };

  const openInvoiceDetail = async (invoiceId) => {
    try {
      const detail = await getInvoiceDetail(invoiceId);
      setDetailData(detail);
      setDetailOpen(true);
    } catch { toast.error('Failed to load invoice detail'); }
  };

  const handleArchivePaid = async () => {
    try {
      const r = await archivePaidInvoices();
      toast.success(`${r.archived_count} invoice(s) archived`);
      fetchData();
    } catch { toast.error('Failed to archive'); }
  };

  // Add to draft invoice
  const openAddToDraft = async (clientId, bookingIds) => {
    try {
      const drafts = await getClientDraftInvoices(clientId);
      if (!drafts.length) { toast.error('No draft invoices for this client. Create one first.'); return; }
      setAddToDraftClientId(clientId);
      setAddToDraftBookingIds(bookingIds);
      setDraftInvoices(drafts);
      setSelectedDraftId(drafts[0]?.id || '');
      setAddToDraftOpen(true);
    } catch { toast.error('Failed to load draft invoices'); }
  };

  const handleAddToDraft = async () => {
    if (!selectedDraftId || !addToDraftBookingIds.length) return;
    try {
      const r = await addBookingsToInvoice(selectedDraftId, { client_id: addToDraftClientId, booking_ids: addToDraftBookingIds });
      toast.success(r.message);
      setAddToDraftOpen(false);
      setSelectedBookings(prev => ({ ...prev, [addToDraftClientId]: [] }));
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add bookings'); }
  };

  // Manual invoice
  const addManualItem = () => setManualInvoiceItems([...manualInvoiceItems, { description: '', quantity: 1, unit_price: '' }]);
  const updateManualItem = (i, f, v) => { const u = [...manualInvoiceItems]; u[i][f] = v; setManualInvoiceItems(u); };
  const removeManualItem = (i) => setManualInvoiceItems(manualInvoiceItems.filter((_, idx) => idx !== i));
  const manualTotal = () => manualInvoiceItems.reduce((s, it) => s + ((parseFloat(it.unit_price) || 0) * (parseInt(it.quantity) || 1) * 100), 0);

  const handleCreateManualInvoice = async () => {
    if (!manualInvoiceClient) { toast.error('Select a client'); return; }
    const valid = manualInvoiceItems.filter(i => i.description && i.unit_price);
    if (!valid.length) { toast.error('Add at least one item'); return; }
    try {
      await createInvoice({ recipient_type: 'client', client_id: manualInvoiceClient, notes: manualInvoiceNotes || null, is_prepayment: manualInvoiceIsPrepayment,
        items: valid.map(i => ({ description: i.description, quantity: parseInt(i.quantity) || 1, unit_price_cents: Math.round(parseFloat(i.unit_price) * 100) })),
      });
      toast.success(manualInvoiceIsPrepayment ? 'Prepayment invoice created' : 'Invoice created');
      setManualInvoiceOpen(false);
      setManualInvoiceClient(''); setManualInvoiceItems([{ description: '', quantity: 1, unit_price: '' }]); setManualInvoiceNotes(''); setManualInvoiceIsPrepayment(false);
      fetchData();
    } catch { toast.error('Failed to create invoice'); }
  };

  // Drag
  const handleDragStart = (e, g, col) => setDraggedItem({ ...g, sourceColumn: col });
  const handleDragOver = (e) => e.preventDefault();
  const handleDrop = (e, target) => {
    e.preventDefault();
    if (!draggedItem) return;
    if ((draggedItem.sourceColumn === 'invoiced' || draggedItem.sourceColumn === 'outstanding') && target === 'paid' && draggedItem.invoice_id) openPaymentSheet(draggedItem.invoice_id);
    setDraggedItem(null);
  };

  // Paid filter
  const getFilteredPaid = () => {
    const data = lifecycleData.paid || [];
    if (paidFilter === 'all') return data;
    const now = new Date();
    let cutoff;
    if (paidFilter === '1w') cutoff = subWeeks(now, 1);
    else if (paidFilter === '2w') cutoff = subWeeks(now, 2);
    else if (paidFilter === '1m') cutoff = subMonths(now, 1);
    else if (paidFilter === '3m') cutoff = subMonths(now, 3);
    else return data;
    return data.filter(g => {
      const b = g.bookings?.[0];
      if (!b?.start_at_utc) return true;
      try { return parseISO(b.start_at_utc) >= cutoff; } catch { return true; }
    });
  };

  const getColumnBalance = (id, data) => {
    let total;
    if (id === 'outstanding') {
      // Outstanding: show remaining balance (total - paid - credits)
      total = data.reduce((s, c) => {
        const invTotal = c.invoice_total_cents || c.total_cents || 0;
        const settled = (c.amount_paid_cents || 0) + (c.credit_applied_cents || 0);
        return s + (invTotal - settled);
      }, 0);
    } else {
      total = data.reduce((s, c) => s + (c.total_cents || 0), 0);
    }
    switch (id) {
      case 'upcoming': return { text: formatMoney(total), className: 'text-foreground' };
      case 'complete': return total > 0 ? { text: `-${formatMoney(total)}`, className: 'text-rose-600' } : { text: formatMoney(0), className: 'text-muted-foreground' };
      case 'invoiced': return total > 0 ? { text: `-${formatMoney(total)}`, className: 'text-rose-600' } : { text: formatMoney(0), className: 'text-muted-foreground' };
      case 'outstanding': return total > 0 ? { text: `-${formatMoney(total)}`, className: 'text-rose-600' } : { text: formatMoney(0), className: 'text-muted-foreground' };
      case 'prepaid_credit': return total > 0 ? { text: formatMoney(total), className: 'text-emerald-600' } : { text: formatMoney(0), className: 'text-muted-foreground' };
      case 'paid': return total > 0 ? { text: formatMoney(total), className: 'text-emerald-600' } : { text: formatMoney(0), className: 'text-muted-foreground' };
      default: return { text: formatMoney(total), className: '' };
    }
  };

  // ============ RENDER: Client accordion (upcoming/complete) ============
  const renderClientAccordion = (cg, columnId) => {
    const bids = cg.bookings.map(b => b.id);
    const sel = selectedBookings[cg.client_id] || [];
    const allSel = sel.length === bids.length && bids.length > 0;
    const name = getDisplayName(cg);
    const sub = getSubtitle(cg);
    const expanded = isExpanded(`client-${cg.client_id}`);

    return (
      <div key={cg.client_id} className="border rounded-lg mb-2 bg-background overflow-hidden" data-testid={`client-group-${cg.client_id}`}>
        {/* Client header - clickable to expand */}
        <button onClick={() => toggleExpand(`client-${cg.client_id}`)} className="w-full px-3 py-2.5 flex items-center justify-between hover:bg-muted/50 transition-colors">
          <div className="flex items-center gap-2 min-w-0">
            {expanded ? <ChevronDown className="w-3.5 h-3.5 shrink-0 text-muted-foreground" /> : <ChevronRight className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />}
            <div className="text-left min-w-0">
              <p className="font-medium text-sm truncate">{name}</p>
              {sub && <p className="text-xs text-muted-foreground truncate">{sub}</p>}
            </div>
          </div>
          <div className="text-right shrink-0">
            <p className="font-mono font-semibold text-sm">{formatMoney(cg.total_cents)}</p>
            <p className="text-xs text-muted-foreground">
              {cg.bookings.length <= 2
                ? cg.bookings.map(b => b.service_name).join(', ')
                : `${cg.bookings.length} sessions`
              }
            </p>
          </div>
        </button>

        {expanded && (
          <div className="border-t">
            {/* Select all + Create/Add Invoice */}
            <div className="px-3 py-2 bg-muted/30 border-b">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Checkbox checked={allSel} onCheckedChange={() => selectAllForClient(cg.client_id, bids)} data-testid={`select-all-${cg.client_id}`} />
                  <span className="text-xs text-muted-foreground">Select all</span>
                </div>
                <span className="text-xs text-muted-foreground">{sel.length} selected</span>
              </div>
              {sel.length > 0 && (
                <div className="flex gap-1.5 mt-2">
                  <Button size="sm" className="flex-1 text-xs" onClick={() => openCreateInvoice(cg.client_id, sel)} data-testid={`create-invoice-${cg.client_id}`}>
                    <Plus className="w-3 h-3 mr-1" />
                    {columnId === 'upcoming' ? 'New Prepaid' : 'New Invoice'}
                  </Button>
                  <Button size="sm" variant="outline" className="flex-1 text-xs" onClick={() => openAddToDraft(cg.client_id, sel)} data-testid={`add-to-draft-${cg.client_id}`}>
                    <FileText className="w-3 h-3 mr-1" />
                    Add to Draft
                  </Button>
                </div>
              )}
            </div>
            {/* Booking list */}
            <div className="divide-y">
              {cg.bookings.map(b => {
                const isSel = sel.includes(b.id);
                return (
                  <div key={b.id} className={`px-3 py-2.5 flex items-start gap-2 hover:bg-muted/30 ${isSel ? 'bg-primary/5' : ''}`} data-testid={`booking-item-${b.id}`}>
                    <Checkbox checked={isSel} onCheckedChange={() => toggleBookingSelection(cg.client_id, b.id)} className="mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: b.service_color }} />
                        <p className="text-sm font-medium truncate">{b.service_name}</p>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        <Clock className="w-3 h-3 inline mr-1" />{format(parseISO(b.start_at_utc), 'EEE, MMM d · h:mm a')}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <span className="font-mono text-sm">{formatMoney(b.rate_cents)}</span>
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => { setTransferringBooking(b); setTransferOpen(true); }} title="Transfer"><UserPlus className="w-3 h-3" /></Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  // ============ RENDER: Invoice card (invoiced/outstanding/prepaid_credit/paid) ============
  const renderInvoiceCard = (ig, columnId) => {
    const name = getDisplayName(ig);
    const sub = getSubtitle(ig);
    const isManual = ig.is_manual;
    const isCredit = ig.bookings?.[0]?.is_credit;
    const isPrepayment = ig.is_prepayment;
    const isDraggable = columnId === 'invoiced' || columnId === 'outstanding';
    const expanded = isExpanded(`inv-${ig.invoice_id}`);
    const amtPaid = ig.amount_paid_cents || 0;
    const creditApplied = ig.credit_applied_cents || 0;
    const invTotal = ig.invoice_total_cents || ig.total_cents || 0;
    const totalSettled = amtPaid + creditApplied;
    const remainingBalance = invTotal - totalSettled;
    const isPartSettled = totalSettled > 0 && totalSettled < invTotal && columnId === 'outstanding';
    const isPaid = columnId === 'paid';

    // Display amount: for outstanding, show remaining balance; otherwise show invoice total
    const displayAmount = columnId === 'outstanding' ? remainingBalance : ig.total_cents;

    // Paid column: always show double-height summary (no expand needed for basics)
    if (isPaid) {
      return (
        <div key={ig.invoice_id} className="border rounded-lg mb-2 bg-background overflow-hidden cursor-pointer hover:border-emerald-300 transition-colors" onClick={() => ig.invoice_id && openInvoiceDetail(ig.invoice_id)} data-testid={`invoice-card-${ig.invoice_id}`}>
          <div className="px-3 py-2.5">
            <div className="flex items-center justify-between mb-1">
              <p className="font-medium text-sm truncate">{name}</p>
              <span className="font-mono font-semibold text-sm text-emerald-600">{formatMoney(ig.total_cents)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs font-mono text-muted-foreground">{ig.invoice_number || 'Credit'}</span>
              <div className="flex items-center gap-1">
                {isManual && <Badge variant="outline" className="text-[10px] px-1 py-0 bg-purple-500/10 text-purple-600">Manual</Badge>}
                <Badge className="bg-emerald-500/10 text-emerald-600 text-[10px] px-1.5 py-0"><CheckCircle2 className="w-3 h-3 mr-0.5" /> Paid</Badge>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div
        key={ig.invoice_id}
        draggable={isDraggable}
        onDragStart={(e) => handleDragStart(e, ig, columnId)}
        className={`border rounded-lg mb-2 bg-background overflow-hidden ${isDraggable ? 'cursor-grab active:cursor-grabbing' : ''}`}
        data-testid={`invoice-card-${ig.invoice_id}`}
      >
        {/* Header */}
        <button onClick={() => toggleExpand(`inv-${ig.invoice_id}`)} className="w-full px-3 py-2 text-left hover:bg-muted/50 transition-colors">
          <div className="flex items-center justify-between mb-0.5">
            <div className="flex items-center gap-1.5 min-w-0">
              {expanded ? <ChevronDown className="w-3.5 h-3.5 shrink-0 text-muted-foreground" /> : <ChevronRight className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />}
              <p className="font-medium text-sm truncate">{name}</p>
            </div>
            <span className="font-mono font-semibold text-sm shrink-0 ml-2">{formatMoney(displayAmount)}</span>
          </div>
          <div className="flex items-center justify-between pl-5">
            {sub && <p className="text-xs text-muted-foreground truncate">{sub}</p>}
            <div className="flex items-center gap-1 shrink-0">
              {ig.invoice_number && <span className="text-[11px] font-mono text-muted-foreground">{ig.invoice_number}</span>}
              {columnId === 'invoiced' && <Badge variant="outline" className="text-[10px] px-1 py-0 bg-slate-500/10 text-slate-600">Draft</Badge>}
              {isManual && <Badge variant="outline" className="text-[10px] px-1 py-0 bg-purple-500/10 text-purple-600">Manual</Badge>}
              {isPrepayment && <Badge variant="outline" className="text-[10px] px-1 py-0 bg-teal-500/10 text-teal-600">Prepaid</Badge>}
              {isCredit && <Badge variant="outline" className="text-[10px] px-1 py-0 bg-emerald-500/10 text-emerald-600">Credit</Badge>}
              {isPartSettled && <Badge variant="outline" className="text-[10px] px-1 py-0 bg-amber-500/10 text-amber-600">Part Paid</Badge>}
            </div>
          </div>
        </button>

        {/* Expanded content */}
        {expanded && (
          <>
            <div className="divide-y border-t">
              {ig.bookings.map(item => (
                <div key={item.id} className="px-3 py-2 flex items-start justify-between">
                  <div className="flex items-start gap-2 min-w-0">
                    <div className="w-2.5 h-2.5 rounded-full shrink-0 mt-1" style={{ backgroundColor: item.service_color }} />
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{item.service_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {item.is_manual || item.is_credit ? (
                          item.source_invoice_number ? `From ${item.source_invoice_number}` : item.notes ? item.notes.substring(0, 40) : 'Custom'
                        ) : (
                          <><Clock className="w-3 h-3 inline mr-1" />{format(parseISO(item.start_at_utc), 'EEE, MMM d · h:mm a')}</>
                        )}
                      </p>
                    </div>
                  </div>
                  <span className="font-mono text-sm shrink-0 ml-2">{formatMoney(item.rate_cents)}</span>
                </div>
              ))}
            </div>

            {isPartSettled && (
              <div className="px-3 py-1.5 bg-amber-500/5 border-t text-xs space-y-0.5">
                <div className="flex justify-between"><span className="text-muted-foreground">Invoice Total</span><span className="font-mono">{formatMoney(invTotal)}</span></div>
                {amtPaid > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Paid</span><span className="font-mono text-emerald-600">{formatMoney(amtPaid)}</span></div>}
                {creditApplied > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Credit Applied</span><span className="font-mono text-teal-600">{formatMoney(creditApplied)}</span></div>}
                <div className="flex justify-between border-t border-amber-200 pt-0.5"><span className="text-muted-foreground font-medium">Remaining</span><span className="font-mono text-rose-600">-{formatMoney(remainingBalance)}</span></div>
              </div>
            )}

            <div className="px-3 py-1.5 border-t bg-muted/20 flex items-center justify-between">
              {ig.invoice_id && (
                <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => openInvoiceDetail(ig.invoice_id)}><Eye className="w-3 h-3 mr-1" /> View</Button>
              )}
              <div className="flex flex-wrap items-center gap-1">
                {columnId === 'invoiced' && (
                  <>
                    <Button variant="ghost" size="sm" className="h-6 px-1.5 text-[10px]" onClick={() => window.open(getInvoicePdfUrl(ig.invoice_id), '_blank')} data-testid={`pdf-invoice-${ig.invoice_id}`}><Download className="w-3 h-3 mr-0.5" />PDF</Button>
                    <Button variant="ghost" size="sm" className="h-6 px-1.5 text-[10px]" onClick={() => handleEditInvoice(ig.invoice_id)} data-testid={`edit-invoice-${ig.invoice_id}`}><Edit className="w-3 h-3 mr-0.5" />Edit</Button>
                    <Button variant="default" size="sm" className="h-6 px-1.5 text-[10px]" onClick={() => handleSendInvoice(ig.invoice_id)} data-testid={`send-invoice-${ig.invoice_id}`}><Send className="w-3 h-3 mr-0.5" />Send</Button>
                  </>
                )}
                {columnId === 'outstanding' && (
                  <>
                    <Button variant="ghost" size="sm" className="h-6 px-1.5 text-[10px]" onClick={() => window.open(getInvoicePdfUrl(ig.invoice_id), '_blank')} data-testid={`pdf-outstanding-${ig.invoice_id}`}><Download className="w-3 h-3 mr-0.5" />PDF</Button>
                    <Button variant="ghost" size="sm" className="h-6 px-1.5 text-[10px]" onClick={() => handleSendInvoice(ig.invoice_id)} data-testid={`resend-invoice-${ig.invoice_id}`}><Send className="w-3 h-3 mr-0.5" />Resend</Button>
                    <Button variant="ghost" size="sm" className="h-6 px-1.5 text-[10px]" onClick={() => openApplyCredit(ig)} data-testid={`apply-credit-${ig.invoice_id}`}><CreditCard className="w-3 h-3 mr-0.5" />Credit</Button>
                    <Button variant="default" size="sm" className="h-6 px-1.5 text-[10px]" onClick={() => openPaymentSheet(ig.invoice_id)} data-testid={`pay-invoice-${ig.invoice_id}`}><DollarSign className="w-3 h-3 mr-0.5" />Pay</Button>
                  </>
                )}
                {columnId === 'prepaid_credit' && (
                  <Badge className={`text-xs ${isCredit ? 'bg-emerald-500/10 text-emerald-600' : 'bg-teal-500/10 text-teal-600'}`}>
                    <Wallet className="w-3 h-3 mr-1" /> {isCredit ? 'Available' : 'Prepaid'}
                  </Badge>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  // ============ Column content ============
  const renderColumnContent = (columnId, columnData) => {
    if (!columnData.length) return <div className="text-center text-muted-foreground text-sm py-8">No items</div>;
    if (columnId === 'upcoming' || columnId === 'complete') {
      return <div className="space-y-0">{columnData.map(cg => renderClientAccordion(cg, columnId))}</div>;
    }
    return <div className="space-y-0">{columnData.map(ig => renderInvoiceCard(ig, columnId))}</div>;
  };

  return (
    <div className="flex flex-col h-full" data-testid="invoices-page">
      <div className="px-8 py-4 border-b border-border bg-background/95 backdrop-blur-sm">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-heading font-bold">Invoicing</h1>
          </div>
          <div className="flex items-center gap-2">
            {/* Column visibility toggles */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Eye className="w-4 h-4 mr-2" /> Columns
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {COLUMNS.map(col => (
                  <DropdownMenuItem key={col.id} onClick={() => toggleColumnVisibility(col.id)} className="flex items-center gap-2">
                    <Checkbox checked={!hiddenColumns.includes(col.id)} />
                    <div className={`w-2 h-2 rounded-full ${col.color}`} />
                    <span className="text-sm">{col.title}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button onClick={() => setManualInvoiceOpen(true)} data-testid="new-invoice-btn"><Plus className="w-4 h-4 mr-2" /> New Invoice</Button>
          </div>
        </div>
        
        {/* Summary Bar */}
        <div className="flex gap-4 mt-3 overflow-x-auto pb-1">
          {COLUMNS.filter(col => !hiddenColumns.includes(col.id)).map(col => {
            const isPaid = col.id === 'paid';
            const data = isPaid ? getFilteredPaid() : (lifecycleData[col.id] || []);
            const count = data.reduce((s, c) => s + (c.bookings?.length || 0), 0);
            const bal = getColumnBalance(col.id, data);
            return (
              <div key={col.id} className="flex items-center gap-2 px-3 py-1.5 bg-muted/40 rounded-full text-xs whitespace-nowrap">
                <div className={`w-2 h-2 rounded-full ${col.color}`} />
                <span className="font-medium">{col.title.split(' ')[0]}</span>
                <Badge variant="outline" className="font-mono text-[10px] h-5">{count}</Badge>
                <span className={`font-mono font-semibold ${bal.className}`}>{bal.text}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex-1 overflow-x-auto p-4">
        {loading ? (
          <div className="flex items-center justify-center h-full"><p className="text-muted-foreground">Loading...</p></div>
        ) : (
          <div className="flex gap-3 h-full min-w-max">
            {COLUMNS.filter(col => !hiddenColumns.includes(col.id)).map(col => {
              const isPaid = col.id === 'paid';
              const data = isPaid ? getFilteredPaid() : (lifecycleData[col.id] || []);
              const count = data.reduce((s, c) => s + (c.bookings?.length || 0), 0);
              const bal = getColumnBalance(col.id, data);

              return (
                <div key={col.id} className="w-60 flex flex-col bg-muted/20 rounded-lg" onDragOver={handleDragOver} onDrop={(e) => handleDrop(e, col.id)} data-testid={`column-${col.id}`}>
                  <div className="p-2.5 border-b bg-background rounded-t-lg">
                    <div className="flex items-center justify-between mb-0.5">
                      <div className="flex items-center gap-2">
                        <div className={`w-2.5 h-2.5 rounded-full ${col.color}`} />
                        <h3 className="font-semibold text-xs">{col.title}</h3>
                      </div>
                      <Badge variant="secondary" className="font-mono text-[10px] h-5">{count}</Badge>
                    </div>
                    <p className={`text-xs font-mono font-semibold ${bal.className}`}>{bal.text}</p>
                    {isPaid && (
                      <div className="flex items-center gap-1 mt-1.5">
                        <Select value={paidFilter} onValueChange={setPaidFilter}>
                          <SelectTrigger className="h-6 text-[10px] flex-1" data-testid="paid-filter"><SelectValue /></SelectTrigger>
                          <SelectContent>{PAID_FILTERS.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
                        </Select>
                        <Button variant="outline" size="sm" className="h-6 w-6 p-0" onClick={handleArchivePaid} title="Archive all paid" data-testid="archive-paid-btn">
                          <Archive className="w-3 h-3" />
                        </Button>
                      </div>
                    )}
                  </div>
                  <div className="flex-1 overflow-y-auto p-1.5">
                    {renderColumnContent(col.id, data)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Invoice Dialog */}
      <Dialog open={createInvoiceOpen} onOpenChange={setCreateInvoiceOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Invoice</DialogTitle>
            <DialogDescription>Create an invoice from {invoiceBookingIds.length} selected booking(s)</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateInvoiceOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateInvoice} data-testid="confirm-create-invoice">Create Invoice</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Invoice Sheet */}
      <Sheet open={editInvoiceOpen} onOpenChange={setEditInvoiceOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader><SheetTitle>Edit Invoice {editingInvoice?.invoice_number}</SheetTitle><SheetDescription>Modify discount and notes</SheetDescription></SheetHeader>
          {editingInvoice && (
            <div className="mt-6 space-y-6">
              <div className="p-4 bg-muted/30 rounded-lg"><p className="text-sm text-muted-foreground">Subtotal</p><p className="text-xl font-mono font-bold">{formatMoney(editingInvoice.subtotal_cents)}</p></div>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><Percent className="w-4 h-4" /> Discount</Label>
                  <div className="flex gap-2">
                    <Select value={invoiceDiscountType} onValueChange={setInvoiceDiscountType}>
                      <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="percent">%</SelectItem><SelectItem value="fixed">$</SelectItem></SelectContent>
                    </Select>
                    <Input type="number" step="0.01" min="0" value={invoiceDiscount} onChange={(e) => setInvoiceDiscount(e.target.value)} className="flex-1" />
                  </div>
                  {invoiceDiscount && parseFloat(invoiceDiscount) > 0 && <p className="text-sm text-muted-foreground">Discount: {formatMoney(calculateDiscountCents(editingInvoice.subtotal_cents))}</p>}
                </div>
                <div className="space-y-2"><Label>Discount Description</Label><Input value={invoiceDiscountDesc} onChange={(e) => setInvoiceDiscountDesc(e.target.value)} placeholder="e.g., Loyalty discount" /></div>
                <div className="space-y-2"><Label>Notes</Label><Textarea value={invoiceNotes} onChange={(e) => setInvoiceNotes(e.target.value)} /></div>
              </div>
              <div className="p-4 bg-primary/5 rounded-lg"><p className="text-sm text-muted-foreground">Total</p><p className="text-2xl font-mono font-bold">{formatMoney(Math.max(0, editingInvoice.subtotal_cents - calculateDiscountCents(editingInvoice.subtotal_cents)))}</p></div>
              <div className="flex gap-2"><Button variant="outline" className="flex-1" onClick={() => setEditInvoiceOpen(false)}>Cancel</Button><Button className="flex-1" onClick={handleSaveInvoice} data-testid="save-invoice-btn">Save</Button></div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Payment Sheet */}
      <Sheet open={paymentSheetOpen} onOpenChange={setPaymentSheetOpen}>
        <SheetContent className="w-full sm:max-w-md">
          <SheetHeader><SheetTitle>Record Payment</SheetTitle><SheetDescription>Enter payment details</SheetDescription></SheetHeader>
          {payingInvoice && (
            <div className="mt-6 space-y-4">
              <div className="p-4 bg-muted/30 rounded-lg space-y-1">
                <p className="text-sm text-muted-foreground">Invoice {payingInvoice.invoice_number}</p>
                <div className="flex justify-between"><span className="text-sm">Total</span><span className="font-mono">{formatMoney(payingInvoice.total_cents)}</span></div>
                {(payingInvoice.amount_paid_cents > 0 || payingInvoice.credit_applied_cents > 0) && (
                  <>
                    {payingInvoice.amount_paid_cents > 0 && <div className="flex justify-between"><span className="text-sm text-muted-foreground">Paid</span><span className="font-mono text-emerald-600">{formatMoney(payingInvoice.amount_paid_cents)}</span></div>}
                    {payingInvoice.credit_applied_cents > 0 && <div className="flex justify-between"><span className="text-sm text-muted-foreground">Credit</span><span className="font-mono text-teal-600">{formatMoney(payingInvoice.credit_applied_cents)}</span></div>}
                  </>
                )}
                <div className="flex justify-between border-t pt-1"><span className="text-sm font-medium">Due</span><span className="font-mono font-bold text-rose-600">-{formatMoney(payingInvoice.total_cents - (payingInvoice.amount_paid_cents || 0) - (payingInvoice.credit_applied_cents || 0))}</span></div>
              </div>
              <div className="space-y-2">
                <Label>Payment Method</Label>
                <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                  <SelectTrigger data-testid="payment-method-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{PAYMENT_METHODS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                </Select>
                {paymentMethod === 'other' && <Input value={paymentMethodOther} onChange={(e) => setPaymentMethodOther(e.target.value)} placeholder="Enter method" />}
              </div>
              <div className="space-y-2">
                <Label>Amount ($)</Label>
                <Input type="number" step="0.01" value={paymentAmount} onChange={(e) => setPaymentAmount(e.target.value)} data-testid="payment-amount-input" />
                {(() => {
                  const rem = payingInvoice.total_cents - (payingInvoice.amount_paid_cents || 0) - (payingInvoice.credit_applied_cents || 0);
                  const amt = parseFloat(paymentAmount) * 100;
                  if (amt > 0 && amt < rem) return <p className="text-xs text-amber-600">Partial payment — invoice stays outstanding.</p>;
                  if (amt > rem) return <p className="text-xs text-teal-600">Overpayment of {formatMoney(amt - rem)} will be credited.</p>;
                  return null;
                })()}
              </div>
              <div className="space-y-2"><Label>Reference (optional)</Label><Input value={paymentReference} onChange={(e) => setPaymentReference(e.target.value)} /></div>
              <Button className="w-full" onClick={handleRecordPayment} data-testid="confirm-payment-btn">Record Payment</Button>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Apply Credit Dialog */}
      <Dialog open={applyCreditOpen} onOpenChange={setApplyCreditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><CreditCard className="w-5 h-5 text-teal-500" /> Apply Credit</DialogTitle>
            <DialogDescription>Apply available credit to this invoice.</DialogDescription>
          </DialogHeader>
          {applyCreditInvoice && (
            <div className="space-y-4 py-2">
              <div className="p-3 bg-muted rounded-lg"><p className="text-sm text-muted-foreground">Invoice {applyCreditInvoice.invoice_number}</p><p className="font-mono font-bold">{formatMoney(applyCreditInvoice.total_cents - (applyCreditInvoice.amount_paid_cents || 0) - (applyCreditInvoice.credit_applied_cents || 0))} remaining</p></div>
              <div className="space-y-2">
                <Label>Select Credit</Label>
                <Select value={selectedCreditId} onValueChange={(v) => { setSelectedCreditId(v); const c = clientCredits.find(x => x.id === v); if (c) { const rem = applyCreditInvoice.total_cents - (applyCreditInvoice.amount_paid_cents || 0) - (applyCreditInvoice.credit_applied_cents || 0); setCreditApplyAmount((Math.min(c.amount_cents, rem) / 100).toFixed(2)); } }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{clientCredits.map(c => <SelectItem key={c.id} value={c.id}>{c.description} — {formatMoney(c.amount_cents)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Amount ($)</Label><Input type="number" step="0.01" value={creditApplyAmount} onChange={(e) => setCreditApplyAmount(e.target.value)} /></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setApplyCreditOpen(false)}>Cancel</Button><Button onClick={handleApplyCredit} data-testid="confirm-apply-credit">Apply</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer Dialog */}
      <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-500" /> Transfer Booking</DialogTitle><DialogDescription>Move this booking to another client.</DialogDescription></DialogHeader>
          {transferringBooking && (
            <div className="space-y-4 py-2">
              <div className="p-3 bg-muted rounded-lg"><p className="font-medium">{transferringBooking.service_name}</p><p className="text-sm text-muted-foreground">{format(parseISO(transferringBooking.start_at_utc), 'PPP p')} — {formatMoney(transferringBooking.rate_cents)}</p></div>
              <div className="space-y-2"><Label>Transfer to</Label>
                <Select value={transferTargetClient} onValueChange={setTransferTargetClient}><SelectTrigger><SelectValue placeholder="Select client" /></SelectTrigger><SelectContent>{clients.filter(c => c.is_active !== false && c.id !== transferringBooking.client_id).map(c => <SelectItem key={c.id} value={c.id}>{c.client_type === 'business' && c.business_name ? c.business_name : c.name}</SelectItem>)}</SelectContent></Select>
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setTransferOpen(false)}>Cancel</Button><Button onClick={handleTransfer} disabled={!transferTargetClient}>Transfer</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add to Draft Invoice Dialog */}
      <Dialog open={addToDraftOpen} onOpenChange={setAddToDraftOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add to Draft Invoice</DialogTitle>
            <DialogDescription>Add {addToDraftBookingIds.length} booking(s) to an existing draft invoice.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Select Draft Invoice</Label>
              <Select value={selectedDraftId} onValueChange={setSelectedDraftId}>
                <SelectTrigger data-testid="select-draft-invoice"><SelectValue placeholder="Select invoice" /></SelectTrigger>
                <SelectContent>
                  {draftInvoices.map(d => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.invoice_number} — {formatMoney(d.total_cents)}{d.is_prepayment ? ' (Prepaid)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <p className="text-sm text-muted-foreground">The selected bookings will be added as new line items on this draft invoice.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddToDraftOpen(false)}>Cancel</Button>
            <Button onClick={handleAddToDraft} disabled={!selectedDraftId} data-testid="confirm-add-to-draft">Add to Invoice</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Manual Invoice Sheet */}
      <Sheet open={manualInvoiceOpen} onOpenChange={setManualInvoiceOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader><SheetTitle>Create Invoice</SheetTitle><SheetDescription>Create a manual or prepayment invoice</SheetDescription></SheetHeader>
          <div className="mt-6 space-y-6">
            <div className="space-y-2"><Label>Client *</Label>
              <Select value={manualInvoiceClient} onValueChange={setManualInvoiceClient}><SelectTrigger data-testid="manual-invoice-client"><SelectValue placeholder="Select a client" /></SelectTrigger><SelectContent>{clients.filter(c => c.is_active !== false).map(c => <SelectItem key={c.id} value={c.id}>{c.client_type === 'business' && c.business_name ? c.business_name : c.name}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="flex items-center justify-between p-3 border rounded-lg"><div><Label className="text-sm font-medium">Prepayment / Buy Credit</Label><p className="text-xs text-muted-foreground">Amount becomes credit when paid</p></div><Switch checked={manualInvoiceIsPrepayment} onCheckedChange={setManualInvoiceIsPrepayment} data-testid="prepayment-toggle" /></div>
            <div className="space-y-3">
              <div className="flex items-center justify-between"><Label>Items</Label><Button type="button" variant="outline" size="sm" onClick={addManualItem}><Plus className="w-3 h-3 mr-1" /> Add</Button></div>
              {manualInvoiceItems.map((item, i) => (
                <Card key={i}><CardContent className="p-3 space-y-2">
                  <Input placeholder="Description" value={item.description} onChange={(e) => updateManualItem(i, 'description', e.target.value)} data-testid={`manual-item-desc-${i}`} />
                  <div className="flex gap-2">
                    <Input type="number" placeholder="Qty" value={item.quantity} onChange={(e) => updateManualItem(i, 'quantity', e.target.value)} min={1} className="w-20" data-testid={`manual-item-qty-${i}`} />
                    <div className="relative flex-1"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span><Input type="number" step="0.01" value={item.unit_price} onChange={(e) => updateManualItem(i, 'unit_price', e.target.value)} className="pl-7" data-testid={`manual-item-price-${i}`} /></div>
                    {manualInvoiceItems.length > 1 && <Button type="button" variant="ghost" size="icon" onClick={() => removeManualItem(i)}><Trash2 className="w-4 h-4" /></Button>}
                  </div>
                </CardContent></Card>
              ))}
            </div>
            <div className="space-y-2"><Label>Notes (optional)</Label><Textarea value={manualInvoiceNotes} onChange={(e) => setManualInvoiceNotes(e.target.value)} data-testid="manual-invoice-notes" /></div>
            <div className="p-4 bg-primary/5 rounded-lg"><p className="text-sm text-muted-foreground">{manualInvoiceIsPrepayment ? 'Prepayment Total' : 'Total'}</p><p className="text-2xl font-mono font-bold">{formatMoney(manualTotal())}</p></div>
            <div className="flex gap-2"><Button variant="outline" className="flex-1" onClick={() => setManualInvoiceOpen(false)}>Cancel</Button><Button className="flex-1" onClick={handleCreateManualInvoice} data-testid="create-manual-invoice-btn">{manualInvoiceIsPrepayment ? 'Create Prepayment' : 'Create Invoice'}</Button></div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Invoice Detail Sheet */}
      <Sheet open={detailOpen} onOpenChange={setDetailOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader><SheetTitle>Invoice Detail</SheetTitle><SheetDescription>{detailData?.invoice_number}</SheetDescription></SheetHeader>
          {detailData && (
            <div className="mt-6 space-y-6">
              {/* Client info */}
              <div className="p-4 bg-muted/30 rounded-lg space-y-1">
                <p className="text-sm text-muted-foreground">Client</p>
                <p className="font-medium">{detailData.client?.business_name || detailData.client?.name || 'Unknown'}</p>
                {detailData.client?.contact_person && <p className="text-xs text-muted-foreground">{detailData.client.contact_person}</p>}
                {detailData.client?.email && <p className="text-xs text-muted-foreground">{detailData.client.email}</p>}
              </div>

              {/* Invoice summary */}
              <div className="p-4 bg-muted/30 rounded-lg space-y-2">
                <div className="flex justify-between"><span className="text-sm text-muted-foreground">Invoice Date</span><span className="text-sm">{format(parseISO(detailData.issue_date_utc), 'PPP')}</span></div>
                <div className="flex justify-between"><span className="text-sm text-muted-foreground">Status</span><Badge variant="outline" className="capitalize">{detailData.status}</Badge></div>
                {detailData.is_prepayment && <div className="flex justify-between"><span className="text-sm text-muted-foreground">Type</span><Badge variant="outline" className="bg-teal-500/10 text-teal-600">Prepayment</Badge></div>}
              </div>

              {/* Line items */}
              <div>
                <h4 className="text-sm font-medium mb-2">Items</h4>
                <div className="border rounded-lg divide-y">
                  {detailData.items.map((item, i) => (
                    <div key={i} className="px-3 py-2.5">
                      <div className="flex items-start justify-between">
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            {item.service_color && <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: item.service_color }} />}
                            <p className="text-sm font-medium">{item.service_name || item.description}</p>
                          </div>
                          {item.start_at_utc && (
                            <p className="text-xs text-muted-foreground mt-0.5">
                              <Clock className="w-3 h-3 inline mr-1" />
                              {format(parseISO(item.start_at_utc), 'EEE, MMM d, yyyy · h:mm a')}
                              {item.end_at_utc && ` — ${format(parseISO(item.end_at_utc), 'h:mm a')}`}
                            </p>
                          )}
                          {item.quantity > 1 && <p className="text-xs text-muted-foreground">{item.quantity} x {formatMoney(item.unit_price_cents)}</p>}
                        </div>
                        <span className="font-mono text-sm shrink-0 ml-2">{formatMoney(item.amount_cents)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Totals */}
              <div className="p-4 bg-primary/5 rounded-lg space-y-1.5">
                <div className="flex justify-between"><span className="text-sm">Subtotal</span><span className="font-mono text-sm">{formatMoney(detailData.subtotal_cents)}</span></div>
                {detailData.discount_cents > 0 && (
                  <div className="flex justify-between text-rose-600"><span className="text-sm">Discount{detailData.discount_description ? ` (${detailData.discount_description})` : ''}</span><span className="font-mono text-sm">-{formatMoney(detailData.discount_cents)}</span></div>
                )}
                <div className="flex justify-between border-t pt-1.5"><span className="font-medium">Total</span><span className="font-mono font-bold text-lg">{formatMoney(detailData.total_cents)}</span></div>
                {detailData.credit_applied_cents > 0 && <div className="flex justify-between"><span className="text-sm text-teal-600">Credit Applied</span><span className="font-mono text-sm text-teal-600">-{formatMoney(detailData.credit_applied_cents)}</span></div>}
                {detailData.amount_paid_cents > 0 && <div className="flex justify-between"><span className="text-sm text-emerald-600">Amount Paid</span><span className="font-mono text-sm text-emerald-600">{formatMoney(detailData.amount_paid_cents)}</span></div>}
                {detailData.status !== 'paid' && (
                  <div className="flex justify-between border-t pt-1"><span className="font-medium text-rose-600">Balance Due</span><span className="font-mono font-bold text-rose-600">-{formatMoney(detailData.total_cents - (detailData.amount_paid_cents || 0) - (detailData.credit_applied_cents || 0))}</span></div>
                )}
              </div>

              {/* Payments */}
              {detailData.payments?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2">Payments</h4>
                  <div className="border rounded-lg divide-y">
                    {detailData.payments.map((p, i) => (
                      <div key={i} className="px-3 py-2 flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium capitalize">{p.method === 'other' ? (p.method_other || 'Other') : p.method.replace('_', ' ')}</p>
                          <p className="text-xs text-muted-foreground">{format(parseISO(p.paid_at_utc), 'PPP')}{p.reference ? ` · ${p.reference}` : ''}</p>
                        </div>
                        <span className="font-mono text-sm text-emerald-600">{formatMoney(p.amount_cents)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {detailData.notes && (
                <div><h4 className="text-sm font-medium mb-1">Notes</h4><p className="text-sm text-muted-foreground">{detailData.notes}</p></div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
